import * as React from "react";
import { HoloBadge } from "@/components/Holo";
import { cn } from "@/lib/utils";
import { PillKind, pillFor, PillInput } from "../packageStatus";

export interface StatusPillProps {
  pkg: PillInput;
  size?: "small" | "medium";
  /** "overlay" sits on a thumbnail: frosted white with a colored dot, readable over any image. */
  appearance?: "tint" | "overlay";
}

const DOT_CLASS: Record<PillKind, string> = {
  generating: "bg-primary",
  updating: "bg-primary",
  draft: "bg-amber-500",
  published: "bg-emerald-500",
  failed: "bg-red-500",
  archived: "bg-slate-400",
};

const SIZE_CLASS: Record<NonNullable<StatusPillProps["size"]>, string> = {
  small: "px-2.5 py-0.5 text-xs",
  // Same padding and type as HoloBadge "md", so it lines up with the Global badge in the editor header.
  medium: "px-2.5 py-1 text-xs",
};

/** The colored dot the overlay pill leads with, for other badges that carry a status. */
export const StatusDot: React.FC<{ kind: PillKind }> = ({ kind }) => (
  <span className={cn("size-1.5 rounded-full", DOT_CLASS[kind])} aria-hidden="true" />
);

/** A status label, not a control: no hover or focus styles. */
export const StatusPill: React.FC<StatusPillProps> = ({ pkg, size = "small", appearance = "tint" }) => {
  const pill = pillFor(pkg);
  const overlay = appearance === "overlay";

  if (pill.kind === "updating") {
    return (
      <HoloBadge
        label={pill.label}
        size={size === "small" ? "sm" : "md"}
        className={overlay ? "shadow-sm" : pill.className}
      />
    );
  }

  return (
    <span
      data-kind={pill.kind}
      className={cn(
        "inline-flex items-center whitespace-nowrap rounded-full border border-transparent font-semibold",
        SIZE_CLASS[size],
        overlay ? "gap-1.5 bg-white/90 text-slate-800 shadow-sm backdrop-blur-sm" : pill.className
      )}
    >
      {overlay && <StatusDot kind={pill.kind} />}
      {pill.label}
    </span>
  );
};
